import {
	SalesOrderDTO,
	SearchPendingSalesOrdersRequest,
	SalesOrderType,
	SearchPendingSalesOrdersResponse,
	AddSalesOrderPaymentsRequest,
	AddSalesOrderPaymentsResponse,
	PaymentDTO,
	BuyerCustomerDTO,
	ChangeSalesOrderCustomerRequest,
	DocumentEmailOptionsDTO,
	GetSalesOrderEmailOptionsRequest,
	GetSalesOrderEmailOptionsResponse,
	SendDocumentEmailResultDTO,
	SendSalesOrderEmailRequest,
	SendSalesOrderEmailResponse,
	CloseSalesOrderFromPosRequest,
	CloseSalesOrderFromPosResponse,
	InvoiceParamsFromPosDTO,
	CancelSalesOrderRequest,
	TicketLocationDTO,
	ReopenSalesOrderFromPosRequest,
	ReopenSalesOrderFromPosResponse,
	SaleDTO,
	TicketDTO,
	GetPendingStandardSalesOrdersByDeliveryDateRequest,
	GetPendingStandardSalesOrdersByDeliveryDateResponse,
	DocumentType,
	GetAllSeriesRequest,
	GetAllSeriesResponse,
	GetSalesOrderDraftRequest,
	GetSalesOrderDraftResponse,
	CreateSalesOrderFromPosRequest,
	SalesOrderParamsFromPosDTO,
	CreateSalesOrderFromPosResponse,
	TicketLineDTO,
	SetTicketLineCashDiscountRequest,
	GetNewTakeOutOrderCountRequest,
	GetNewTakeOutOrderCountResponse,
	GetPendingSalesOrderByIdRequest,
	GetPendingSalesOrderByIdResponse,
	SetSalesOrderTableCompanionsRequest,
	CreateSalesOrderRefundFromPosRequest,
	CreateSalesOrderRefundFromPosResponse
} from 'core/api';
import { Bus, SessionProvider } from 'core/infrastructure';

const process = Bus.buildProcessFn('/bus/');

export async function findByText(searchText: string): Promise<SalesOrderDTO[]> {
	const request = new SearchPendingSalesOrdersRequest({ searchText, types: [SalesOrderType.Standard] });
	return process(request, (response: SearchPendingSalesOrdersResponse) => response.Orders);
}

export async function addPayments(orderId: number, payments: PaymentDTO[], prepayAmount: number) {
	const request = new AddSalesOrderPaymentsRequest({ orderId, payments, prepayAmount });
	return process(request, (response: AddSalesOrderPaymentsResponse) => response.Sale);
}

export async function changeCustomer(salesOrderId: number, customer: BuyerCustomerDTO) {
	const request = new ChangeSalesOrderCustomerRequest({ salesOrderId, customer });
	await process(request);
}

export async function getEmailOptions(salesOrderId: number): Promise<DocumentEmailOptionsDTO> {
	const request = new GetSalesOrderEmailOptionsRequest({ salesOrderId });
	return await process(request, (response: GetSalesOrderEmailOptionsResponse) => response.Options);
}

export async function sendEmail(options: DocumentEmailOptionsDTO): Promise<SendDocumentEmailResultDTO> {
	const request = new SendSalesOrderEmailRequest({ options });
	return await process(request, (response: SendSalesOrderEmailResponse) => response.Result);
}

export async function createInvoice(orderId: number, params: InvoiceParamsFromPosDTO): Promise<SaleDTO> {
	const request = new CloseSalesOrderFromPosRequest({ orderId, params });
	return process(request, (response: CloseSalesOrderFromPosResponse) => response.Sale);
}

export async function cancel(orderId: number): Promise<void> {
	const request = new CancelSalesOrderRequest({
		orderId,
		overridePosId: null,
		overrideBusinessDay: null,
		overrideCreationDate: null,
		audit: true
	});
	await process(request);
}

export function reopen(orderId: number, location: TicketLocationDTO): Promise<TicketDTO> {
	const request = new ReopenSalesOrderFromPosRequest({
		salesOrderId: orderId,
		params: { Location: location, TargetTicketId: null }
	});
	return process(request, (response: ReopenSalesOrderFromPosResponse) => response.Ticket);
}

export async function reopenInTicket(orderId: number, location: TicketLocationDTO, targetTicketId: number): Promise<TicketDTO> {
	const request = new ReopenSalesOrderFromPosRequest({
		salesOrderId: orderId,
		params: { Location: location, TargetTicketId: targetTicketId }
	});
	return process(request, (response: ReopenSalesOrderFromPosResponse) => response.Ticket);
}

export async function getByDeliveryDate(deliveryDate: Date): Promise<SalesOrderDTO[]> {
	const request = new GetPendingStandardSalesOrdersByDeliveryDateRequest({
		deliveryDate
	});
	return process(request, (response: GetPendingStandardSalesOrdersByDeliveryDateResponse) => response.SalesOrders);
}

export async function getDraftByTicketId(ticketId: number) {
	const request = new GetSalesOrderDraftRequest({ ticketId });
	return process(request, (response: GetSalesOrderDraftResponse) => response.Draft);
}

export function create(ticketId: number, params: SalesOrderParamsFromPosDTO) {
	const request = new CreateSalesOrderFromPosRequest({ ticketId, params });
	return process(request, (response: CreateSalesOrderFromPosResponse) => response.Sale);
}

export async function createRefund(salesOrderId: number, params: InvoiceParamsFromPosDTO): Promise<SaleDTO> {
	const request = new CreateSalesOrderRefundFromPosRequest({ salesOrderId, params });
	return process(request, (response: CreateSalesOrderRefundFromPosResponse) => response.Sale);
}

export async function setLineCashDiscount(ticketId: number, line: TicketLineDTO, discount: number): Promise<void> {
	const request = new SetTicketLineCashDiscountRequest({ ticketId, ticketLineId: line.Id, discount });
	await process(request);
}

export async function getSeries() {

	const request = new GetAllSeriesRequest();
	const series = await process(request, (response: GetAllSeriesResponse) => response.All);

	return series
		.filter(x => x.Type === DocumentType.SalesOrder);
}

export async function getNewTakeOutOrderCount() {
	const request = new GetNewTakeOutOrderCountRequest();
	return process(request, (response: GetNewTakeOutOrderCountResponse) => response.Count);
}

export async function getById(orderId: number): Promise<SalesOrderDTO> {
	const request = new GetPendingSalesOrderByIdRequest({ orderId, isTrainingMode: SessionProvider.getCurrent().IsTrainingMode });
	return process(request, (response: GetPendingSalesOrderByIdResponse) => response.Order);
}

export function setTableCompanions(salesOrderId: number, tableCompanions: Nullable<number>) {
	const request = new SetSalesOrderTableCompanionsRequest({ salesOrderId, tableCompanions });
	return process(request);
}

